import { createContext, ReactNode, useContext, useEffect } from 'react';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { CheckmarkIcon, ExclamationIcon } from '../../assets/icons';
import { ResponsiveSpacing, useTabbing } from '../../utils';
import { Animate } from '../Animate';
import { Headings } from '../Headings';
import { Container, Flex } from '../Layout';
import { useMediaMin } from '../Media';
import { createScrollReference, scrollToElement } from '../Scroll';
import { PanelStatus } from './types';

const rootCircle = css`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 2.813rem;
  width: 2.25rem;
  height: 2.25rem;
  bottom: 0;
  margin: auto 0;
  border-radius: 50%;
  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    left: 1.5625rem;
  }
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    left: 1.25rem;
    margin: 0;
    transform: translate(0, -50%);
  }
`;

const CompletedCircle = styled.div<{ isOpen: boolean }>`
  ${rootCircle}
  background: ${props => (props.isOpen ? theme.colors.white : theme.colors.babyGreen)};
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    border: 2px solid ${theme.colors.babyGreen};
  }
`;

const WarningCircle = styled.div`
  ${rootCircle}
  background: ${theme.colors.babyPink};
  width: 2.5rem;
  height: 2.5rem;
`;

const PanelHeader = styled.button<{
  status: PanelStatus;
  isOpen: boolean;
  isTabbing: boolean;
  zIndex?: number;
  $togglable: boolean;
  marginTop?: ResponsiveSpacing;
}>`
  position: relative;
  background-color: ${theme.colors.white};
  margin-top: ${props => (props.marginTop ? props.marginTop : '2.5rem')};
  width: 100%;
  text-align: left;
  border: 0;
  padding: 0;
  appearance: none;
  outline: none;

  ${props =>
    props.status === PanelStatus.Active &&
    css`
      background-color: ${theme.colors.darkBlue};
    `}

  ${props =>
    props.status === PanelStatus.Completed &&
    css`
      background-color: ${props.isOpen ? theme.colors.babyGreen : theme.colors.white};

      ${props.$togglable &&
      css`
        :hover {
          background-color: ${theme.colors.babyGreen};
          ${CompletedCircle} {
            background-color: ${theme.colors.white};
          }
        }
      `}
    `}

  ${props =>
    props.status === PanelStatus.Warning &&
    css`
      background-color: ${theme.colors.amaranth};
    `}

    ${props =>
    props.isTabbing &&
    props.$togglable &&
    css`
      &:focus {
        outline: solid 2px;
        outline-offset: -2px;
      }
    `}

  cursor: ${({ $togglable }) => ($togglable ? 'pointer' : 'default')};
  ${props => props.zIndex !== undefined && `z-index: ${props.zIndex};`}
`;

const PanelContext = createContext<Pick<PanelProps, 'isOpen' | 'status' | 'zIndex' | 'onToggle'>>({
  isOpen: false,
  status: PanelStatus.Inactive,
});

const animationTransitionTime = 0.6;
const animationDelayTime = 0.25;

type PanelProps = {
  testId?: string;
  name?: string;
  previousPanelName?: string;
  status: PanelStatus;
  isOpen: boolean;
  enableScrolling?: boolean;
  onToggle?: (status: PanelStatus) => void;
  zIndex?: number;
  children: ReactNode;
};

export const Panel = ({
  testId,
  name = '',
  previousPanelName = '',
  enableScrolling = true,
  status,
  isOpen,
  onToggle,
  zIndex,
  children,
}: PanelProps) => {
  const isLargeDesktop = useMediaMin(theme.breakpoint.xlg);
  const scrollOffset = isLargeDesktop ? 0 : -70;

  useEffect(() => {
    if (enableScrolling && isOpen) {
      if (status === PanelStatus.Active && previousPanelName) {
        scrollTo(previousPanelName);
      } else if (status === PanelStatus.Completed && name) {
        scrollTo(name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const scrollTo = (ref: string) => {
    scrollToElement(ref, animationTransitionTime * 1000, scrollOffset, animationDelayTime * 1000);
  };

  return (
    <PanelContext.Provider value={{ isOpen, status, onToggle, zIndex }}>
      <div data-testid={testId}>
        {enableScrolling && name && createScrollReference(name)}
        {children}
      </div>
    </PanelContext.Provider>
  );
};

Panel.Header = ({
  testId,
  children,
  marginTop,
}: {
  testId?: string;
  children: ReactNode;
  marginTop?: ResponsiveSpacing;
}) => {
  const { status, onToggle, isOpen, zIndex } = useContext(PanelContext);
  const isTabbing = useTabbing();
  const togglable = !!onToggle && status === PanelStatus.Completed;

  return (
    <PanelHeader
      data-testid={testId}
      status={status}
      isOpen={isOpen}
      onClick={togglable ? () => onToggle(status) : undefined}
      isTabbing={isTabbing}
      disabled={!togglable}
      zIndex={zIndex}
      $togglable={togglable}
      marginTop={marginTop}
    >
      <Container paddingLeft={[20, 80, 103]} paddingRight={[20, 80, 103]} paddingTop={28} paddingBottom={28}>
        {status === PanelStatus.Completed && (
          <CompletedCircle isOpen={isOpen}>
            <CheckmarkIcon color="petroleumGreen" />
          </CompletedCircle>
        )}
        {status === PanelStatus.Warning && (
          <WarningCircle>
            <ExclamationIcon color="burgundy" />
          </WarningCircle>
        )}
        <Headings.H3
          color={
            status === PanelStatus.Completed && isOpen
              ? 'black90'
              : status === PanelStatus.Completed
                ? 'black70'
                : status === PanelStatus.Inactive
                  ? 'black50'
                  : 'white'
          }
        >
          {children}
        </Headings.H3>
      </Container>
    </PanelHeader>
  );
};

Panel.Body = ({ children }: { children: ReactNode }) => {
  const { isOpen } = useContext(PanelContext);

  return (
    <Animate.Height isTriggered={isOpen} transitionTime={animationTransitionTime} delayTime={animationDelayTime}>
      <Flex backgroundColor="white">{children}</Flex>
    </Animate.Height>
  );
};

Panel.MainSection = ({ children }: { children: ReactNode }) => (
  <Container
    flexBasis={784}
    flexGrow={1}
    paddingLeft={[20, 80, 103]}
    paddingRight={[20, 80, 103]}
    paddingTop={[30, 20, 40]}
    paddingBottom={80}
    maxWidth={'100%'}
  >
    {children}
  </Container>
);

Panel.AsideSection = ({ children }: any) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  return isDesktop ? <Flex flexBasis={412}>{children}</Flex> : null;
};

type PanelListProps = {
  children: React.ReactElement | React.ReactElement[];
  maxWidth?: ResponsiveSpacing;
  paddingLeft?: ResponsiveSpacing;
  paddingRight?: ResponsiveSpacing;
  marginLeft?: ResponsiveSpacing;
  marginRight?: ResponsiveSpacing;
};

Panel.List = ({
  children,
  maxWidth = 1196,
  paddingLeft = [0, 20, 0],
  paddingRight = [0, 20, 0],
  marginLeft = 'auto',
  marginRight = 'auto',
}: PanelListProps) => (
  <Container
    maxWidth={maxWidth}
    paddingLeft={paddingLeft}
    paddingRight={paddingRight}
    marginLeft={marginLeft}
    marginRight={marginRight}
  >
    {children}
  </Container>
);
